import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Gamepad2, Lock, Loader2 } from 'lucide-react';
import { getConfiguredPublicGamesPin, PUBLIC_GAMES_SESSION_KEY } from '../../config/publicGames';

const TIR_A_LA_CORDE_COVER = `${process.env.PUBLIC_URL || ''}/games/tir-a-la-corde/cover.png`;
/** داخل مربع بطاقة اللعبة فقط */
const TUG_OF_WAR_KIDS_BG = `${process.env.PUBLIC_URL || ''}/games/tir-a-la-corde/bg-tug-kids.jpg`;

function readUnlockedFromStorage() {
  try {
    return sessionStorage.getItem(PUBLIC_GAMES_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function setUnlockedInStorage(value) {
  try {
    if (value) sessionStorage.setItem(PUBLIC_GAMES_SESSION_KEY, '1');
    else sessionStorage.removeItem(PUBLIC_GAMES_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function PublicTirALaCordeCard() {
  const { t } = useTranslation();
  return (
    <Link
      to="/public/games/tir-a-la-corde"
      className="group block overflow-hidden rounded-[1.35rem] border-[3px] border-sky-400/50 bg-white shadow-[0_8px_0_0_rgba(14,165,233,0.2)] transition hover:-translate-y-0.5 hover:border-sky-400 hover:shadow-[0_8px_0_0_rgba(56,189,248,0.28)] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
    >
      <div className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden" aria-hidden>
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${TUG_OF_WAR_KIDS_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-white/35" />
        <img
          src={TIR_A_LA_CORDE_COVER}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          className="relative z-[1] h-[88%] w-[92%] max-h-full object-contain object-center drop-shadow-lg"
        />
        <div className="absolute bottom-2 end-2 z-[2] rounded-lg bg-black/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/95 backdrop-blur-sm">
          {t('publicGames.play')}
        </div>
      </div>
      <div className="border-t border-slate-900/5 bg-gradient-to-b from-white to-sky-50/90 px-3 py-3">
        <p className="line-clamp-2 text-center text-sm font-bold leading-snug text-slate-900 group-hover:text-sky-900">
          {t('publicGames.tirALaCordeTitle')}
        </p>
      </div>
    </Link>
  );
}

/**
 * صفحة عامة: ألعاب القسم — تتطلب رمز PIN من 4 أرقام (بدون تسجيل دخول).
 */
const PublicGames = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const [unlocked, setUnlocked] = useState(() => readUnlockedFromStorage());
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const expectedPin = getConfiguredPublicGamesPin();

  const tryUnlock = useCallback(() => {
    const entered = pin.replace(/\D/g, '').slice(0, 4);
    if (entered.length !== 4) {
      setError(true);
      return;
    }
    setSubmitting(true);
    setError(false);
    window.setTimeout(() => {
      if (entered === expectedPin) {
        setUnlockedInStorage(true);
        setUnlocked(true);
        setPin('');
      } else {
        setError(true);
      }
      setSubmitting(false);
    }, 120);
  }, [pin, expectedPin]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Enter' && !unlocked && pin.replace(/\D/g, '').length === 4) {
        tryUnlock();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [unlocked, pin, tryUnlock]);

  const handleLock = () => {
    setUnlockedInStorage(false);
    setUnlocked(false);
    setPin('');
    setError(false);
  };

  const onPinChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(v);
    if (error) setError(false);
  };

  return (
    <div
      className="min-h-[100dvh] w-full bg-gradient-to-b from-sky-100/95 via-violet-50/80 to-amber-50/70 px-4 py-10 sm:py-14"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className={`mx-auto w-full ${unlocked ? 'max-w-4xl' : 'max-w-lg'}`}>
        {!unlocked ? (
          <div className="overflow-hidden rounded-[1.75rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_16px_50px_-24px_rgba(59,130,246,0.45)]">
            <div className="border-b border-slate-900/8 bg-gradient-to-r from-violet-600/90 to-indigo-600/95 px-6 py-8 text-center text-white">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-2 ring-white/30">
                <Lock className="h-8 w-8" strokeWidth={2.25} aria-hidden />
              </div>
              <h1 className="text-2xl font-black tracking-tight sm:text-3xl">{t('publicGames.title')}</h1>
              <p className="mt-2 text-sm font-semibold text-white/90 sm:text-base">{t('publicGames.subtitle')}</p>
            </div>

            <div className="space-y-5 px-6 py-8 sm:px-8">
              <div>
                <label htmlFor="public-games-pin" className="mb-2 block text-sm font-bold text-slate-700">
                  {t('publicGames.pinLabel')}
                </label>
                <input
                  id="public-games-pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={4}
                  value={pin}
                  onChange={onPinChange}
                  placeholder={t('publicGames.pinPlaceholder')}
                  className="w-full rounded-2xl border-[3px] border-slate-900/12 bg-slate-50/90 px-4 py-4 text-center font-mono text-3xl font-black tracking-[0.5em] text-slate-900 shadow-inner placeholder:text-slate-300 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-300/80"
                  aria-invalid={error}
                  aria-describedby={error ? 'pin-error' : undefined}
                />
                {error && (
                  <p id="pin-error" className="mt-2 text-sm font-bold text-red-600" role="alert">
                    {t('publicGames.pinError')}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={tryUnlock}
                disabled={submitting || pin.replace(/\D/g, '').length !== 4}
                className="flex w-full min-h-[3rem] items-center justify-center gap-2 rounded-2xl border-[3px] border-slate-900/15 bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-3.5 text-base font-black text-white shadow-[0_6px_0_0_rgba(15,23,42,0.15)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : null}
                {t('publicGames.submit')}
              </button>

              <p className="text-center text-xs font-medium text-slate-500">{t('publicGames.footerNote')}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg">
                  <Gamepad2 className="h-7 w-7" strokeWidth={2.25} aria-hidden />
                </span>
                <div>
                  <h1 className="text-2xl font-black text-slate-900 sm:text-3xl">{t('publicGames.gamesHeading')}</h1>
                </div>
              </div>
              <button
                type="button"
                onClick={handleLock}
                className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-xl border-2 border-slate-900/15 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50"
              >
                <Lock className="h-4 w-4" aria-hidden />
                {t('publicGames.lockAgain')}
              </button>
            </div>

            <div className="mx-auto w-full max-w-3xl">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <PublicTirALaCordeCard />
              </div>
            </div>

            <p className="text-center text-xs font-medium text-slate-500">{t('publicGames.footerNote')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicGames;
